import { useAtom } from "jotai";
import { KeyRound, Settings, ShieldCheck, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageHeader from "../../../shared/components/PageHeader";
import { AppLanguage, languageAtom } from "../../../shared/state/preferencesAtoms";
import {
  changeAdminPassword,
  disableTwoFactor,
  getTwoFactorStatus,
  setupTwoFactor,
  updateAdminProfile,
  verifyTwoFactorSetup,
} from "../api/profileApi";
import { useAuth } from "../hooks/useAuth";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object") {
    const maybeAxios = error as {
      response?: { data?: { message?: string | string[] }; status?: number };
    };
    const message = maybeAxios.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(", ");
    }
    if (typeof message === "string") {
      return message;
    }
    if (maybeAxios.response?.status === 401) {
      return "Necesitas ingresar la contraseña actual correcta.";
    }
  }
  return fallback;
};

const ProfileSettingsPage = () => {
  const { session, updateSessionUser } = useAuth();
  const [language, setLanguage] = useAtom(languageAtom);
  const [name, setName] = useState(session.user?.name ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const roles = useMemo(() => session.user?.roles ?? [], [session.user?.roles]);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);
  const [setupData, setSetupData] = useState<{ qrCodeUrl: string; secret: string } | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [twoFactorMessage, setTwoFactorMessage] = useState<string | null>(null);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [isSaving2FA, setIsSaving2FA] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);

  const fetchTwoFactorStatus = useCallback(async () => {
    try {
      setTwoFactorLoading(true);
      const { twoFactorEnabled: enabled } = await getTwoFactorStatus();
      setTwoFactorEnabled(enabled);
    } catch {
      // Silently fail — status defaults to false
    } finally {
      setTwoFactorLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTwoFactorStatus();
  }, [fetchTwoFactorStatus]);

  const handleProfileSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setProfileError("El nombre no puede estar vacío.");
      setProfileMessage(null);
      return;
    }

    try {
      setIsSavingProfile(true);
      setProfileError(null);
      setProfileMessage(null);
      const updated = await updateAdminProfile({ username: trimmed });
      updateSessionUser({ name: updated.username, email: updated.email });
      setProfileMessage("Nombre actualizado correctamente.");
    } catch (error) {
      setProfileError(
        getErrorMessage(error, "No pudimos actualizar el perfil. Intenta nuevamente.")
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError("Las nuevas contraseñas no coinciden.");
      setPasswordMessage(null);
      return;
    }

    try {
      setIsSavingPassword(true);
      setPasswordError(null);
      setPasswordMessage(null);
      await changeAdminPassword({ currentPassword, newPassword });
      setPasswordMessage("Contraseña actualizada correctamente.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      setPasswordError(
        getErrorMessage(error, "No pudimos actualizar la contraseña. Intenta nuevamente.")
      );
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      setIsSaving2FA(true);
      setTwoFactorError(null);
      setTwoFactorMessage(null);
      const data = await setupTwoFactor();
      setSetupData(data);
      setSetupCode("");
    } catch (error) {
      setTwoFactorError(getErrorMessage(error, "No pudimos iniciar la configuración de 2FA."));
    } finally {
      setIsSaving2FA(false);
    }
  };

  const handleVerifySetup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (setupCode.length !== 6) return;

    try {
      setIsSaving2FA(true);
      setTwoFactorError(null);
      await verifyTwoFactorSetup(setupCode);
      setTwoFactorEnabled(true);
      setSetupData(null);
      setSetupCode("");
      setTwoFactorMessage("Autenticación de dos factores activada correctamente.");
    } catch (error) {
      setTwoFactorError(getErrorMessage(error, "Código inválido. Intenta nuevamente."));
      setSetupCode("");
    } finally {
      setIsSaving2FA(false);
    }
  };

  const handleDisable = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disableCode.length !== 6) return;

    try {
      setIsSaving2FA(true);
      setTwoFactorError(null);
      await disableTwoFactor(disableCode);
      setTwoFactorEnabled(false);
      setShowDisableForm(false);
      setDisableCode("");
      setTwoFactorMessage("Autenticación de dos factores desactivada.");
    } catch (error) {
      setTwoFactorError(getErrorMessage(error, "Código inválido. Intenta nuevamente."));
      setDisableCode("");
    } finally {
      setIsSaving2FA(false);
    }
  };

  const handleCancelSetup = () => {
    setSetupData(null);
    setSetupCode("");
    setTwoFactorError(null);
    setTwoFactorMessage(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Perfil" subtitle="Gestiona los datos de tu cuenta administradora." />

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" className="gap-1.5">
            <User className="h-4 w-4" />
            Información
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5">
            <ShieldCheck className="h-4 w-4" />
            Seguridad
          </TabsTrigger>
          <TabsTrigger value="password" className="gap-1.5">
            <KeyRound className="h-4 w-4" />
            Contraseña
          </TabsTrigger>
          <TabsTrigger value="preferences" className="gap-1.5">
            <Settings className="h-4 w-4" />
            Preferencias
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <section className="rounded-lg border border-slate-800 bg-surface p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-100">Información personal</h2>
            <p className="mt-1 text-sm text-slate-400">
              Actualiza cómo te mostramos dentro del backoffice. El correo y el identificador son
              administrados por los supervisores del sistema.
            </p>

            <form onSubmit={handleProfileSubmit} className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">Nombre</span>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 focus:border-primaryAccent focus:outline-none"
                  placeholder="Nombre visible"
                  disabled={isSavingProfile}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">
                  Correo electrónico
                </span>
                <input
                  type="email"
                  value={session.user?.email ?? "sin-correo"}
                  disabled
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-500"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">
                  Identificador
                </span>
                <input
                  type="text"
                  value={session.user?.id ?? "N/D"}
                  disabled
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-500"
                />
              </label>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">Roles</span>
                <div className="flex flex-wrap gap-2">
                  {roles.length === 0 ? (
                    <span className="rounded-md border border-slate-700 bg-background px-3 py-1 text-xs text-slate-500">
                      Sin roles asignados
                    </span>
                  ) : (
                    roles.map((role) => (
                      <span
                        key={role}
                        className="rounded-md border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold text-primaryAccent"
                      >
                        {role}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="col-span-full flex items-center gap-3">
                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
                >
                  {isSavingProfile ? "Guardando..." : "Guardar cambios"}
                </button>
                {profileMessage ? (
                  <span className="text-xs text-success">{profileMessage}</span>
                ) : null}
                {profileError ? <span className="text-xs text-danger">{profileError}</span> : null}
              </div>
            </form>
          </section>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <section className="rounded-lg border border-slate-800 bg-surface p-6 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-100">
                  Autenticación de dos factores
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Protege tu cuenta con un código de verificación adicional al iniciar sesión.
                </p>
              </div>
              {!twoFactorLoading && (
                <span
                  className={`mt-1 shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                    twoFactorEnabled
                      ? "border border-green-500/30 bg-green-500/10 text-green-400"
                      : "border border-slate-700 bg-slate-800 text-slate-400"
                  }`}
                >
                  {twoFactorEnabled ? "Activo" : "Inactivo"}
                </span>
              )}
            </div>

            {twoFactorLoading ? (
              <div className="mt-6 h-10 animate-pulse rounded-md bg-slate-800" />
            ) : twoFactorEnabled ? (
              <div className="mt-6">
                {!showDisableForm ? (
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-green-500/30 bg-green-500/10">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-400"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <polyline points="9 12 12 15 16 10" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-200">
                        2FA está activo en tu cuenta
                      </p>
                      <p className="text-xs text-slate-400">
                        Se te pedirá un código de verificación cada vez que inicies sesión.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDisableForm(true);
                        setTwoFactorError(null);
                        setTwoFactorMessage(null);
                        setDisableCode("");
                      }}
                      className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-400 transition hover:bg-red-500/20"
                    >
                      Desactivar
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleDisable} className="space-y-4">
                    <p className="text-sm text-slate-400">
                      Ingresa el código de tu aplicación de autenticación para confirmar la
                      desactivación.
                    </p>
                    {twoFactorError && <p className="text-xs text-danger">{twoFactorError}</p>}
                    <div className="flex items-end gap-3">
                      <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase text-slate-500">
                          Código de verificación
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          placeholder="000000"
                          value={disableCode}
                          onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                          className="w-40 rounded-md border border-slate-700 bg-background px-3 py-2 text-center font-mono text-lg tracking-[0.3em] text-slate-200 focus:border-primaryAccent focus:outline-none"
                          autoFocus
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={isSaving2FA || disableCode.length !== 6}
                        className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
                      >
                        {isSaving2FA ? "Desactivando..." : "Confirmar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowDisableForm(false);
                          setDisableCode("");
                          setTwoFactorError(null);
                        }}
                        className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-400 transition hover:text-slate-200"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}
                {twoFactorMessage && (
                  <p className="mt-3 text-xs text-success">{twoFactorMessage}</p>
                )}
              </div>
            ) : setupData ? (
              <div className="mt-6">
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                  <div className="shrink-0 rounded-lg border border-slate-700 bg-white p-2">
                    <img src={setupData.qrCodeUrl} alt="QR Code" className="h-40 w-40" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-slate-200">
                        1. Escanea el código QR con tu aplicación de autenticación
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Usa Google Authenticator, Authy, o cualquier app compatible con TOTP.
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-slate-500">
                        O ingresa el código manualmente
                      </p>
                      <code className="mt-1 block break-all rounded-md border border-slate-700 bg-background px-3 py-2 font-mono text-xs text-slate-300">
                        {setupData.secret}
                      </code>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">
                        2. Ingresa el código de 6 dígitos para verificar
                      </p>
                    </div>

                    {twoFactorError && <p className="text-xs text-danger">{twoFactorError}</p>}

                    <form onSubmit={handleVerifySetup} className="flex items-end gap-3">
                      <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase text-slate-500">
                          Código de verificación
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          placeholder="000000"
                          value={setupCode}
                          onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ""))}
                          className="w-40 rounded-md border border-slate-700 bg-background px-3 py-2 text-center font-mono text-lg tracking-[0.3em] text-slate-200 focus:border-primaryAccent focus:outline-none"
                          autoFocus
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={isSaving2FA || setupCode.length !== 6}
                        className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
                      >
                        {isSaving2FA ? "Verificando..." : "Activar 2FA"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelSetup}
                        className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-400 transition hover:text-slate-200"
                      >
                        Cancelar
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                {twoFactorError && <p className="mb-3 text-xs text-danger">{twoFactorError}</p>}
                {twoFactorMessage && (
                  <p className="mb-3 text-xs text-success">{twoFactorMessage}</p>
                )}
                <button
                  type="button"
                  onClick={handleStartSetup}
                  disabled={isSaving2FA}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
                >
                  {isSaving2FA ? "Iniciando..." : "Configurar 2FA"}
                </button>
              </div>
            )}
          </section>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <section className="rounded-lg border border-slate-800 bg-surface p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-100">Cambiar contraseña</h2>
            <p className="mt-1 text-sm text-slate-400">
              Reemplaza tu contraseña actual ingresando la nueva y confirmándola.
            </p>

            <form onSubmit={handlePasswordSubmit} className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs font-semibold uppercase text-slate-500">
                  Contraseña actual
                </span>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 focus:border-primaryAccent focus:outline-none"
                  placeholder="Introduce tu contraseña actual"
                  disabled={isSavingPassword}
                  required
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">
                  Nueva contraseña
                </span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 focus:border-primaryAccent focus:outline-none"
                  placeholder="Mínimo 4 caracteres"
                  disabled={isSavingPassword}
                  minLength={4}
                  required
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">
                  Confirmar nueva contraseña
                </span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 focus:border-primaryAccent focus:outline-none"
                  placeholder="Repite la nueva contraseña"
                  disabled={isSavingPassword}
                  minLength={4}
                  required
                />
              </label>

              <div className="col-span-full flex items-center gap-3">
                <button
                  type="submit"
                  disabled={isSavingPassword}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
                >
                  {isSavingPassword ? "Actualizando..." : "Actualizar contraseña"}
                </button>
                {passwordMessage ? (
                  <span className="text-xs text-success">{passwordMessage}</span>
                ) : null}
                {passwordError ? (
                  <span className="text-xs text-danger">{passwordError}</span>
                ) : null}
              </div>
            </form>
          </section>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <section className="rounded-lg border border-slate-800 bg-surface p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-100">Preferencias</h2>
            <p className="mt-1 text-sm text-slate-400">
              Ajusta el idioma de la interfaz. Los cambios se aplican de inmediato en este
              dispositivo.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase text-slate-500">Idioma</span>
                <select
                  value={language}
                  onChange={(event) => setLanguage(event.target.value as AppLanguage)}
                  className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 focus:border-primaryAccent focus:outline-none"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="pt-BR">Português (Brasil)</option>
                </select>
              </label>
            </div>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfileSettingsPage;
